// ---------------------------------------------------------------------------
// Consecutive-push tracker for the reschedule ("Get it later") flow.
//
// Context: ~49% of scheduled renewals get pushed (Recharge actions-by-product,
// Sep 2025–Sep 2026), and serial pushing is the main driver of the reorder-rate
// decline. This module counts how many times in a row a member has moved a
// subscription's next charge date LATER, so the new-portal reschedule flow can
// escalate after PUSH_ESCALATION_THRESHOLD pushes (downsize / switch to
// on-demand) instead of silently absorbing a fourth or fifth delay.
//
// Storage is localStorage (per device). That undercounts members who switch
// devices, which only makes the escalation show LESS often — acceptable for a
// nudge, and it keeps this dependency-free. The authoritative per-variant
// numbers come from the `user_events` rows and the Klaviyo "Box Rescheduled"
// event, both of which now carry the push count (see services/recharge.js).
//
// Counts reset when the member moves a date EARLIER, places an Order Now, or
// when the entry goes stale (no push in STALE_AFTER_DAYS — the cycle most
// likely completed with a processed charge, which we can't observe here).
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'wg-reschedule-push-counts';

// Number of prior consecutive pushes after which the reschedule flow escalates
// (i.e. the escalation shows on the 3rd push attempt and beyond).
export const PUSH_ESCALATION_THRESHOLD = 2;

const STALE_AFTER_DAYS = 90;

const readAll = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (err) {
    return {};
  }
};

const writeAll = (map) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (err) {
    // Storage unavailable (private mode / quota) — the member just won't get
    // the escalation nudge; never block the reschedule flow over it.
  }
};

const isStale = (entry) =>
  !entry?.lastPushAt || Date.now() - entry.lastPushAt > STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;

// Consecutive pushes recorded so far for this subscription (0 when none/stale).
export const getPushCount = (subscriptionId) => {
  if (!subscriptionId) return 0;
  const entry = readAll()[String(subscriptionId)];
  if (!entry || isStale(entry)) return 0;
  return Number(entry.count) || 0;
};

// Record a reschedule. `movedLater` increments the streak; moving earlier
// resets it (the member is re-engaging, not stalling). Returns the new count.
export const trackPush = (subscriptionId, movedLater) => {
  if (!subscriptionId) return 0;
  const key = String(subscriptionId);
  const map = readAll();
  if (!movedLater) {
    delete map[key];
    writeAll(map);
    return 0;
  }
  const prev = map[key] && !isStale(map[key]) ? Number(map[key].count) || 0 : 0;
  const count = prev + 1;
  map[key] = { count, lastPushAt: Date.now() };
  writeAll(map);
  return count;
};

// Explicit reset — Order Now processed, ODM switch, etc.
export const resetPushCount = (subscriptionId) => {
  if (!subscriptionId) return;
  const map = readAll();
  delete map[String(subscriptionId)];
  writeAll(map);
};
